import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Facebook, FileText, Linkedin, Loader2, Mail } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://umi-it.com.au";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    axios.get(`${API}/blog/${slug}`).then((res) => setPost(res.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="py-32 bg-white text-center px-4">
        <h1 className="text-3xl font-bold text-[#0B2545]">Article not found</h1>
        <Link to="/blog" data-testid="blog-post-back-missing" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0D9488]">
          <ArrowLeft size={15} aria-hidden="true" /> Back to all articles
        </Link>
      </section>
    );
  }

  if (!post) {
    return (
      <div className="flex justify-center py-32" data-testid="blog-post-loading">
        <Loader2 size={32} className="animate-spin text-[#17B8A6]" aria-label="Loading article" />
      </div>
    );
  }

  const SHORT_TITLES = {
    "microsoft-365-vs-google-workspace-adelaide-smes": "Microsoft 365 vs Google Workspace for Adelaide SMEs",
    "backup-vs-disaster-recovery": "Backup vs Disaster Recovery: The Plain-English Difference",
    "cyber-insurance-what-insurers-demand": "Cyber Insurance: What Insurers Now Ask Small Business",
    "ai-phishing-scams-2026": "AI Phishing in 2026: Scams Hitting SA Businesses",
    "business-internet-nbn-5g-starlink-adelaide": "Business NBN, 5G or Starlink? Choosing Office Internet",
    "spring-clean-your-it-checklist": "Spring Clean Your IT: A 60-Minute Business Checklist",
    "spf-dkim-dmarc-email-spoofing-plain-english": "SPF, DKIM & DMARC: Stop Email Spoofing, Plain English",
    "5-signs-outgrown-it-setup": "5 Signs Your Business Has Outgrown Its IT Setup | UMI",
    "essential-eight-plain-english": "The Essential Eight, Explained in Plain English | UMI",
    "what-managed-it-support-actually-includes": "What Managed IT Support Actually Includes | UMI",
    "windows-10-end-of-support-upgrade-path": "Still on Windows 10? Your Plain-English Upgrade Path",
    "microsoft-365-copilot-for-smes": "Microsoft 365 Copilot for SMEs: Worth It or Hype? | UMI",
    "real-cost-of-it-downtime": "The Real Cost of IT Downtime for a 20-Person Business | UMI",
    "does-your-business-need-a-password-manager": "Does Your Business Need a Password Manager Yet? | UMI",
  };
  const seoTitle = SHORT_TITLES[post.slug] || (post.title.length <= 54 ? `${post.title} | UMI` : post.title);
  const ogImage = `${SITE}/api/og/${post.slug}.png`;
  const shareUrl = (source) =>
    `${SITE}/blog/${post.slug}?utm_source=${source}&utm_medium=social&utm_campaign=blog`;
  const shareLinks = [
    { name: "LinkedIn", Icon: Linkedin, testid: "share-linkedin", newTab: true,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl("linkedin"))}` },
    { name: "Facebook", Icon: Facebook, testid: "share-facebook", newTab: true,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl("facebook"))}` },
    { name: "Email", Icon: Mail, testid: "share-email", newTab: false,
      href: `mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(shareUrl("email"))}` },
  ];

  return (
    <>
      <SEO title={seoTitle} description={post.excerpt} image={ogImage} imageAlt={`UMI IT Services blog – ${post.title}`} />
      {post.status === "draft" && (
        <div data-testid="blog-draft-banner" className="bg-amber-50 border-b border-amber-200 text-amber-800 text-sm text-center py-2.5 px-4 font-medium">
          Draft preview — this article is not publicly listed and won't appear in the blog feed, RSS or sitemap until approved.
        </div>
      )}

      <article className="bg-white pt-28 pb-16 md:pt-36">
        <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto">
          <Link to="/blog" data-testid="blog-post-back" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0D9488] hover:text-[#17B8A6] transition-colors duration-150">
            <ArrowLeft size={15} aria-hidden="true" /> All articles
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">{post.category}</p>
          <h1 data-testid="blog-post-title" className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.15] text-[#0B2545]">{post.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-500">
            <span>{post.author}</span>
            <span aria-hidden="true">·</span>
            <span>{formatDate(post.published_at)}</span>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1.5"><FileText size={13} aria-hidden="true" /> {post.reading_time} min read</span>
          </div>

          {post.image && (
            <img src={post.image} alt={post.title} loading="lazy"
              className="mt-8 rounded-2xl w-full object-cover max-h-[420px] shadow-lg" />
          )}

          <div data-testid="blog-post-content" className="blog-content mt-10" dangerouslySetInnerHTML={{ __html: post.content }} />

          <div data-testid="blog-share" className="mt-12 flex items-center gap-3 border-t border-slate-200 pt-8">
            <span className="text-sm font-semibold text-slate-600 mr-1">Share this article</span>
            {shareLinks.map(({ name, Icon, testid, href, newTab }) => (
              <a
                key={name}
                href={href}
                {...(newTab ? { target: "_blank", rel: "noopener" } : {})}
                aria-label={`Share on ${name}`}
                data-testid={testid}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-300 text-slate-500 hover:text-[#0D9488] hover:border-[#0D9488] transition-colors duration-150"
              >
                <Icon size={17} aria-hidden="true" />
              </a>
            ))}
          </div>

          <div className="mt-12 rounded-2xl bg-[#0B2545] p-6 sm:p-10 text-center">
            <h2 className="text-xl sm:text-2xl font-semibold text-white">Book a free IT health check</h2>
            <p className="mt-3 text-base text-slate-300 max-w-xl mx-auto">Thirty minutes, plain English, zero obligation — we'll review your setup and tell you what's urgent and what can wait.</p>
            <div data-testid="blog-cta-form" className="mt-8 max-w-xl mx-auto rounded-xl bg-white p-5 sm:p-7 text-left">
              <ContactForm formLocation="blog_post" compact />
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogPost;
